import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

function EmployeeCategoryFormModal({ mode, initialValues, employeeTypeOptions = [], onSubmit, onClose }) {
  const [name, setName] = useState(initialValues?.name || "");
  const [employeeTypeId, setEmployeeTypeId] = useState(
    initialValues?.employeeTypeId && initialValues.employeeTypeId !== "-"
      ? String(initialValues.employeeTypeId)
      : ""
  );
  const [statusId, setStatusId] = useState(
    String(Number(initialValues?.statusId) === 1 ? 1 : 2)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isEdit = mode === "edit";

  useEffect(() => {
    setName(initialValues?.name || "");
    setEmployeeTypeId(
      initialValues?.employeeTypeId && initialValues.employeeTypeId !== "-"
        ? String(initialValues.employeeTypeId)
        : ""
    );
    setStatusId(String(Number(initialValues?.statusId) === 1 ? 1 : 2));
    setError("");
  }, [initialValues?.name, initialValues?.employeeTypeId, initialValues?.statusId]);

  async function handleSubmit(event) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Employee Category name is required.");
      return;
    }
    if (trimmedName.length > 100) {
      setError("Employee Category name must be 100 characters or fewer.");
      return;
    }

    if (!employeeTypeId) {
      setError("Employee Type is required.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit({
        name: trimmedName,
        employeeTypeId,
        ...(isEdit ? { statusId: Number(statusId) } : {}),
      });
      onClose();
    } catch (submitError) {
      setError(submitError?.message || "Unable to save Employee Category.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!submitting) onClose();
      }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="employee-category-form-title"
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle id="employee-category-form-title">
          {isEdit ? "Edit Employee Category" : "Add Employee Category"}
        </DialogTitle>
        <DialogContent className="flex flex-col gap-4 pt-2">
          <TextField
            autoFocus
            required
            fullWidth
            label="Employee Category Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 100 }}
            disabled={submitting}
            error={Boolean(error) && !name.trim()}
          />
          <TextField
            select
            required
            fullWidth
            label="Employee Type"
            value={employeeTypeId}
            onChange={(event) => setEmployeeTypeId(event.target.value)}
            disabled={submitting}
            error={Boolean(error) && !employeeTypeId}
          >
            {employeeTypeOptions.map((option) => (
              <MenuItem
                key={option.employee_type_id}
                value={String(option.employee_type_id)}
              >
                {option.employee_type_name}
              </MenuItem>
            ))}
          </TextField>
          {isEdit ? (
            <TextField
              select
              fullWidth
              label="Status"
              value={statusId}
              onChange={(event) => setStatusId(event.target.value)}
              disabled={submitting}
            >
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="2">Inactive</MenuItem>
            </TextField>
          ) : null}
          {error ? (
            <div role="alert" className="text-sm text-red-600">
              {error}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={onClose} disabled={submitting}>
            Cancel Employee Category
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting
              ? "Saving..."
              : isEdit
                ? "Save Employee Category"
                : "Create Employee Category"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EmployeeCategoryFormModal;
